// One-shot capture of the `#token=…` fragment on /invite and /recover (B6 mail links). The
// fragment is removed from the address bar with history.replaceState before it is parsed and
// before the app makes any request, then handed once to the page that owns it. It is never put
// in storage, logs, analytics, an API URL or a return URL; only the page's flow holds it.
import type { FragmentSource } from './flow.ts';

export type FragmentKind = 'invite' | 'recover';
export const INVITE_PATH = '/invite';
export const RECOVER_PATH = '/recover';

export function identityRouteFor(pathname: string): FragmentKind | null {
  if (pathname === INVITE_PATH) return 'invite';
  if (pathname === RECOVER_PATH) return 'recover';
  return null;
}

interface LocationLike {
  pathname: string;
  search: string;
  hash: string;
}
interface HistoryLike {
  state: unknown;
  replaceState(data: unknown, unused: string, url?: string | URL | null): void;
}

export class FragmentVault implements FragmentSource {
  private held: { kind: FragmentKind; token: string | null } | null = null;
  private readonly listeners = new Set<() => void>();

  /**
   * Call at startup and on every `hashchange`. On /invite or /recover any fragment is scrubbed
   * first (the URL keeps only the path), then a token of exactly 43 URL-safe characters is kept.
   * A malformed fragment is still scrubbed and reported as `token: null`.
   */
  capture(location: LocationLike, history: HistoryLike): void {
    const kind = identityRouteFor(location.pathname);
    if (!kind || location.hash === '' || location.hash === '#') {
      if (kind && location.hash === '#') history.replaceState(history.state, '', location.pathname);
      return;
    }
    const fragment = location.hash;
    history.replaceState(history.state, '', location.pathname);
    const match = /^#token=([A-Za-z0-9_-]{43})$/.exec(fragment);
    this.held = { kind, token: match?.[1] ?? null };
    for (const l of [...this.listeners]) l();
  }

  /** Hands the captured token to its page exactly once. */
  take(kind: FragmentKind): { token: string | null } | null {
    if (!this.held || this.held.kind !== kind) return null;
    const held = this.held;
    this.held = null;
    return { token: held.token };
  }

  /** Called when a new fragment is captured while the app is running (a second link in this tab). */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /** Drops an untaken token (for example when the app leaves the route before the page mounts). */
  clear(): void {
    this.held = null;
  }
}
